import { Form, Input, Modal, Typography } from 'antd';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { MangoAtcTokenAdditionalComponents } from '@app//pages/Integrations/IntegrationsList/components/MangoAtcTokenAdditionalComponents';
import {
  ATC_BEELINE,
  ATC_MANGO,
  ERROR_REQUIRED_FIELD,
  MAX_LENGTH_MESSAGE,
  PREFIX,
  RULES_IS_REQUIRE,
  SUCCESS,
  TECH_SUPPORT,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { AtcPhoneNumbersBlock } from '@app/pages/Integrations/IntegrationsList/components/AtcPhoneNumbersBlock';
import { SalesFunnelFormItems } from '@app/pages/Integrations/IntegrationsList/components/SalesFunnelsFormItems';
import {
  type EditMessengerModalProps,
  type MessengerModalFormValues,
  PhoneModes,
} from '@app/pages/Integrations/IntegrationsList/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import {
  useEditSocialIntegrationMutation,
  useGetSocialIntegrationOperatorsQuery,
  useSetSocialIntegrationOperatorsMutation,
  useUpdateAtcBeelineIntegrationMutation,
  useUpdateAtcMangoIntegrationMutation,
} from '@app/services/rtkQuery/SocialIntegrations/api';
import type { NSocialIntegrations } from '@app/services/rtkQuery/SocialIntegrations/types';

import { OperatorsBlock } from '@pages/Integrations/IntegrationsList/components/OperatorsBlock';

import type { DefaultOptionType } from 'antd/es/select';

const { Text } = Typography;

export const EditMessengerModal: React.FC<EditMessengerModalProps> = ({
  isEditMessengerModalOpen,
  setIsEditMessengerModalOpen,
  setEditableIntegration,
  salesFunnels,
  salesFunnelsStages,
  editIntegrationForm,
  setSelectedSalesFunnelId,
  editableIntegration,
  getData,
  token,
  setToken,
}) => {
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<
    string[] | null
  >(null);

  const { activeBranchId } = useUserPrint();
  const [isSaving, setIsSaving] = useState(false);

  const { showNotification } = useNotification();

  // «Техническая поддержка» — системная интеграция (US 11021): разрешено менять
  // только воронку продаж, остальные поля показываем как read-only, операторов
  // не грузим и не сохраняем.
  const isTechSupport = editableIntegration?.SocialType === TECH_SUPPORT;

  const [editSocialIntegration, { isLoading: isEditSocialIntegrationLoading }] =
    useEditSocialIntegrationMutation();
  const [editOperators, { isLoading: isEditOperatorsLoading }] =
    useSetSocialIntegrationOperatorsMutation();

  const [
    editAtcBeelineIntegration,
    { isLoading: isEditAtcBeelineIntegrationLoading },
  ] = useUpdateAtcBeelineIntegrationMutation();
  const [
    editAtcMangoIntegration,
    { isLoading: isEditAtcMangoIntegrationLoading },
  ] = useUpdateAtcMangoIntegrationMutation();
  const {
    data,
    isLoading: isOperatorsLoading,
    isFetching: isOperatorsFetching,
  } = useGetSocialIntegrationOperatorsQuery(
    {
      ActiveBranchId: activeBranchId ?? 0,
      integrationId: editableIntegration?.Id ?? 0,
    },
    {
      skip:
        activeBranchId === null || !editableIntegration?.Id || isTechSupport,
    }
  );

  const isAtc = useMemo(
    () =>
      editableIntegration?.SocialType === ATC_BEELINE ||
      editableIntegration?.SocialType === ATC_MANGO,
    [editableIntegration?.SocialType]
  );

  useEffect(() => {
    if (!isEditMessengerModalOpen || !editableIntegration) return;

    editIntegrationForm.setFieldsValue({
      newName: editableIntegration.SocialIntegrationName ?? '',
      salesFunnel: editableIntegration.SalesFunnelId,
      salesFunnelStage: editableIntegration.SalesFunnelStageId,
      token: editableIntegration.Token || undefined,
    });

    if (editableIntegration.SocialType === ATC_MANGO) {
      const tokenParts = editableIntegration.Token?.split('.') || [];
      const [signatureKey, atcUniqCode, ...rest] = tokenParts;
      const virtualAtcApiAddress = rest.join('.');

      editIntegrationForm.setFieldsValue({
        SignatureKey: signatureKey,
        AtcUniqCode: atcUniqCode,
        VirtualAtcApiAddress: virtualAtcApiAddress,
      });
    }
  }, [isEditMessengerModalOpen, editableIntegration, editIntegrationForm]);

  const handleSalesFunnelChange = useCallback(
    (value: number) => {
      setSelectedSalesFunnelId(value);
      editIntegrationForm.setFieldsValue({ salesFunnelStage: null });
    },
    [editIntegrationForm, setSelectedSalesFunnelId]
  );

  const resetModalState = useCallback(() => {
    setIsEditMessengerModalOpen(false);
    setEditableIntegration(undefined);
    editIntegrationForm.resetFields();
    setSelectedPhoneNumbers(null);
  }, [
    editIntegrationForm,
    setEditableIntegration,
    setIsEditMessengerModalOpen,
  ]);

  const editSocialIntegrationHandler = useCallback(
    async ({
      newName,
      salesFunnel,
      salesFunnelStage,
      phoneNumbersSelector,
    }: MessengerModalFormValues) => {
      if (
        editableIntegration &&
        activeBranchId &&
        salesFunnel &&
        salesFunnelStage
      ) {
        let data:
          | NSocialIntegrations.EditSocialIntegrationRequest
          | NSocialIntegrations.UpdateAtcBeelineIntegrationRequest
          | NSocialIntegrations.UpdateAtcMangoIntegrationRequest = {
          ActiveBranchId: activeBranchId,
          IntegrationId: editableIntegration.Id,
          // «Техническая поддержка»: название не редактируется — шлём исходное
          // значение как есть, чтобы не менять ничего кроме воронки (US 11021).
          Name: isTechSupport
            ? editableIntegration.SocialIntegrationName ?? ''
            : newName,
          SalesFunnelId: salesFunnel,
          SalesFunnelStageId: salesFunnelStage,
        };

        try {
          if (editableIntegration.SocialType === ATC_BEELINE) {
            data = {
              ...data,
              Token: token!,
              Abonents:
                phoneNumbersSelector === PhoneModes.ALL ||
                selectedPhoneNumbers === null
                  ? []
                  : selectedPhoneNumbers,
            };
            await editAtcBeelineIntegration(data).unwrap();
          } else if (editableIntegration.SocialType === ATC_MANGO) {
            data = {
              ...data,
              Token: token!,
              Abonents:
                phoneNumbersSelector === PhoneModes.ALL ||
                selectedPhoneNumbers === null
                  ? []
                  : selectedPhoneNumbers,
            };
            await editAtcMangoIntegration(data).unwrap();
          } else {
            await editSocialIntegration(data).unwrap();
          }
          showNotification('Интеграция обновлена успешно', SUCCESS);
        } catch (error) {
          showNotification(handleCatchError(error));
        } finally {
          await getData();
        }
      }
    },
    [
      editableIntegration,
      activeBranchId,
      showNotification,
      isTechSupport,
      token,
      selectedPhoneNumbers,
      editAtcBeelineIntegration,
      editAtcMangoIntegration,
      editSocialIntegration,
      getData,
    ]
  );

  const editOperatorsHandler = useCallback(
    async ({ operators }: MessengerModalFormValues) => {
      // У «Технической поддержки» операторы не редактируются (US 11021).
      if (isTechSupport) return;

      const selectedOperators =
        data?.filter(({ IsSelected }) => IsSelected) ?? [];
      const currentOperators = selectedOperators.map(({ Id }) => Id);

      const noChangedOperators = isEqual(
        currentOperators || [],
        operators || []
      );

      if (activeBranchId && editableIntegration && !noChangedOperators) {
        try {
          await editOperators({
            ActiveBranchId: activeBranchId,
            IntegrationId: editableIntegration.Id,
            OperatorIds: operators?.filter((id) => typeof id === 'number'),
          }).unwrap();

          showNotification('Операторы обновлены успешно', SUCCESS);
        } catch (error) {
          showNotification(handleCatchError(error));
        }
      }
    },
    [
      activeBranchId,
      editableIntegration,
      data,
      showNotification,
      editOperators,
      isTechSupport,
    ]
  );

  const handleSubmit = useCallback(
    async (values: MessengerModalFormValues) => {
      setIsSaving(true);
      await Promise.all([
        editSocialIntegrationHandler(values),
        editOperatorsHandler(values),
      ]);
      setIsSaving(false);
      resetModalState();
    },
    [editSocialIntegrationHandler, editOperatorsHandler, resetModalState]
  );

  const isLoading = useMemo(
    () =>
      isEditSocialIntegrationLoading ||
      isEditAtcBeelineIntegrationLoading ||
      isEditOperatorsLoading ||
      isEditAtcMangoIntegrationLoading,
    [
      isEditSocialIntegrationLoading,
      isEditAtcBeelineIntegrationLoading,
      isEditOperatorsLoading,
      isEditAtcMangoIntegrationLoading,
    ]
  );

  const operatorsOptions: Array<DefaultOptionType> = useMemo(() => {
    if (isOperatorsLoading || isOperatorsFetching || !data) {
      return [];
    }

    return data.map(({ Id, NameFirst, NameLast }) => ({
      value: Id,
      label: `${NameFirst} ${NameLast}`,
    }));
  }, [data, isOperatorsFetching, isOperatorsLoading]);

  const initOperators = useCallback(() => {
    if (isOperatorsLoading || isOperatorsFetching || !data) {
      return;
    }

    const selectedOperators = data.filter(({ IsSelected }) => IsSelected);

    selectedOperators.forEach(({ Id }) => {
      const handledOperators = [
        ...(editIntegrationForm.getFieldValue('operators') ?? []),
        Id,
      ];

      editIntegrationForm.setFieldsValue({
        operators: handledOperators,
      });
    });
  }, [data, isOperatorsFetching, isOperatorsLoading, editIntegrationForm]);

  useEffect(() => {
    initOperators();
  }, [initOperators]);

  return (
    <Modal
      className={`${PREFIX}edit-messenger-modal`}
      open={isEditMessengerModalOpen}
      onCancel={resetModalState}
      onOk={editIntegrationForm.submit}
      okText="Сохранить"
      cancelText="Отменить"
      title="Редактировать интеграцию"
      okButtonProps={{ loading: isLoading }}
      cancelButtonProps={{ disabled: isLoading }}
      destroyOnHidden
    >
      <Form
        form={editIntegrationForm}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <div className="container">
          <div className="item-container">
            <Text
              strong
              className={`label${isTechSupport ? '' : ' label-require'}`}
            >
              Название
            </Text>
            <Form.Item
              className="editIntegrationFormItem"
              name="newName"
              rules={
                isTechSupport
                  ? [{ max: 140, message: MAX_LENGTH_MESSAGE }]
                  : [
                      { max: 140, message: MAX_LENGTH_MESSAGE },
                      ...RULES_IS_REQUIRE,
                    ]
              }
            >
              <Input disabled={isTechSupport} />
            </Form.Item>
          </div>
          <div className="item-container">
            <SalesFunnelFormItems
              salesFunnels={salesFunnels}
              salesFunnelsStages={salesFunnelsStages}
              onSalesFunnelChange={handleSalesFunnelChange}
              errorRequiredField={ERROR_REQUIRED_FIELD}
              isIntegrationRegistryComponent={true}
            />
          </div>
          {!isTechSupport && (
            <div className="item-container">
              <Text strong className="label">
                Операторы
              </Text>
              <OperatorsBlock
                operators={operatorsOptions}
                isLoading={isOperatorsLoading || isOperatorsFetching}
              />
            </div>
          )}

          {isAtc && (
            <>
              {editableIntegration?.SocialType === ATC_BEELINE && (
                <div className="item-container">
                  <Text strong className="label label-require">
                    Токен авторизации
                  </Text>
                  <Form.Item name="token" rules={[...RULES_IS_REQUIRE]}>
                    <Input
                      size="large"
                      onChange={(e) => setToken(e.target.value)}
                    />
                  </Form.Item>
                </div>
              )}
              {editableIntegration?.SocialType === ATC_MANGO && (
                <MangoAtcTokenAdditionalComponents
                  form={editIntegrationForm}
                  setToken={setToken}
                />
              )}
              <AtcPhoneNumbersBlock
                isOpen={isEditMessengerModalOpen}
                activeBranchId={activeBranchId}
                atcToken={token}
                setSelectedPhoneNumbers={setSelectedPhoneNumbers}
                selectedPhoneNumbers={selectedPhoneNumbers}
                form={editIntegrationForm}
                editableIntegration={editableIntegration}
                integrationSocialType={editableIntegration?.SocialType || ''}
                disabled={
                  isEditAtcBeelineIntegrationLoading ||
                  isEditAtcMangoIntegrationLoading ||
                  isSaving
                }
              />
            </>
          )}
        </div>
      </Form>
    </Modal>
  );
};
